import React, { useMemo } from 'react';
import { Card, Spinner } from 'reactstrap'; // Card is kept for error/empty messages
import {
  Block,
  BlockBetween,
  BlockDes,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  Icon,
  Button, // Assuming Button component is available like in ProfileDealsView
} from '../../../../components/Component'; // Adjust path if necessary
import { formatCurrency, timeAsMilliseconds } from '../../../../utils/Utils';
import { useQuery } from 'react-query';
import useAxiosPrivate from '../../../../hooks/useAxiosPrivate';
import dealQueries from '../../../../queries/dealQueries';
import { Link, useNavigate } from 'react-router-dom';
import { DealCategory, newRoles } from '../../../../utils/envConfig';
import { useSelector } from 'react-redux';

// --- Placeholder for a Skeleton Loader ---
// You might want to create a DealSkeletonLoaderShort.js similar to DealSkeletonLoader
// but perhaps with fewer rows or columns for a "recent items" widget.
// For now, a simple spinner will be used if isLoading is true within the block.
// const DealSkeletonLoaderShort = ({ columns }) => (
//   Array.from({ length: 3 }).map((_, rowIndex) => (
//     <tr className="tb-odr-item" key={`skeleton-row-${rowIndex}`}>
//       {Array.from({ length: columns }).map((_, colIndex) => (
//         <td className="tb-odr-info bg-white text-center" key={`skeleton-cell-${rowIndex}-${colIndex}`}>
//           <span className="placeholder col-8"></span>
//         </td>
//       ))}
//     </tr>
//   ))
// );
// --- End Placeholder ---


// Custom styles for clickable rows (can be moved to CSS classes if preferred)
const clickableRowStyle = {
  cursor: 'pointer',
  transition: 'background-color 0.2s ease',
};

// Style for row hover (can be moved to CSS classes)
const hoverRowStyleJS = { // Renamed to avoid conflict if you have a CSS class
  backgroundColor: '#f8f9fa', // A light grey, adjust as needed
};


// Adapted status badge rendering to match ProfileDealsView style
const RenderRecentDealStatusBadge = React.memo(({ status }) => {
  const baseBadgeClass = "badge badge-sm badge-dot has-bg";
  let colorClass = "primary"; // Default color

  switch (status?.toLowerCase()) {
    case 'completed':
    case 'approved': // Assuming 'approved' is also a success state
      colorClass = 'success';
      break;
    case 'pending':
    case 'pendingapproval': // From ProfileDealsView
    case 'collectingdocuments': // From ProfileDealsView
      colorClass = 'warning';
      break;
    case 'canceled':
    case 'rejected':
      colorClass = 'danger';
      break;
    default:
      colorClass = 'primary'; // Or 'secondary' / 'light' for unknown
  }
  return (
    <span className={`${baseBadgeClass} bg-${colorClass} text-${colorClass}`}>
      {status || 'Unknown'}
    </span>
  );
});

// Format date
const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  try {
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  } catch (e) {
    return 'Invalid Date';
  }
};

const RecentDeals = ({ agentId, limit = 5, viewAllLink }) => {
  const axios = useAxiosPrivate();
  const navigate = useNavigate();

  const agentIdToUse = useMemo(() => agentId, [agentId]);

  const { queryKey, queryFn } = dealQueries.getMostRecentByAgent(
    axios,
    agentIdToUse,
    limit
  );

  const { data, isLoading, isError, error } = useQuery(queryKey, queryFn, {
    staleTime: timeAsMilliseconds({ minutes: 30 }),
    // refetchInterval: timeAsMilliseconds({ minutes: 30 }), // Consider if this is needed for a "recent" list
    refetchOnWindowFocus: false,
    enabled: !!agentIdToUse,
    retry: 1, // Added retry
  });

  const deals = useMemo(() => {
    if (Array.isArray(data)) return data;
    if (data?.deals && Array.isArray(data.deals)) return data.deals;
    if (data?.data && Array.isArray(data.data)) return data.data;
    return [];
  }, [data]);

  const handleRowClick = (dealId) => {
    navigate(`/deal-details/${dealId}`);
  };

  const authUser = useSelector((state) => state?.user?.loggedInUser);

  const deniedPermissions = useMemo(() => {
    if (authUser) {
      return authUser?.specialPermissions?.deny || {};
    }
  }, [authUser]);

  const checkIfLeadsBelongsToAuthUser = (deal) => {
    if (deal.category === DealCategory.SecondaryProperty) {
      return deal?.buyerAssignedAgent?.userId === authUser?.id || deal?.sellerAssignedAgent?.userId === authUser?.id;
    }
    if (deal.category === DealCategory.PrimaryOffplan) {
      return deal?.assignedAgent === authUser?.id;
    }
    return deal?.assignedAgent?.userId === authUser?.id;
  }

  return (
    <div id="recent-deals">
      <Block className="mt-4">
        <BlockHead size={'md'}> {/* md or sm might be more appropriate for a widget */}
          <BlockBetween>
            <BlockHeadContent>
              <BlockTitle tag="h5">Recent Deals</BlockTitle>
              {/* Optional: Add BlockDes if needed */}
              {/* <BlockDes><p>Latest deals associated with this agent.</p></BlockDes> */}
            </BlockHeadContent>
            {viewAllLink && (
              <BlockHeadContent>
                <Button
                  color="primary"
                  outline
                  size="sm" // smaller button for a widget header
                  onClick={(ev) => {
                    ev.preventDefault();
                    navigate(viewAllLink); // Assuming viewAllLink is a route path
                  }}
                >
                  <Icon name={'eye'} />
                  <span className="d-none d-sm-inline-block ms-1">View All</span>
                </Button>
              </BlockHeadContent>
            )}
          </BlockBetween>
        </BlockHead>

        <Block>
          {isLoading ? (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '200px' }}>
              <Spinner color="primary" />
              {/* Or use a skeleton loader: <DealSkeletonLoaderShort columns={tableColumns} /> */}
            </div>
          ) : isError ? (
            <Card className="p-4 shadow-sm"> {/* Using Card for message box */}
              <div className="text-center">
                <Icon name="alert-circle" className="text-danger mb-2" style={{ fontSize: '1.5rem' }} />
                <p className="fs-6 fw-bold text-danger">Failed to load deals.</p>
                <p className="small text-muted">{error?.message || "Please try again later."}</p>
              </div>
            </Card>
          ) : !deals.length ? (
            <Card className="p-4 shadow-sm"> {/* Using Card for message box */}
              <div className="text-center py-3">
                <Icon name="file-text" className="text-muted mb-2" style={{ fontSize: '1.5rem' }} />
                <p className="fs-6 fw-bold">No deals found.</p>
                <p className="small text-muted">This agent does not have any recent deals.</p>
              </div>
            </Card>
          ) : (
            <div className="border rounded bg-white"> {/* Mimic ProfileDealsView table container */}
              <div className="table-responsive">
                <table className="table table-orders"> {/* Removed pe-2, can add if needed */}
                  <thead className="tb-odr-head">
                    <tr className="tb-odr-item">
                      <th className="tb-odr-info text-start ps-3"><span>Reference</span></th>
                      <th className="tb-odr-info text-start d-none d-md-table-cell"><span>Property</span></th>
                      <th className="tb-odr-info text-start d-none d-lg-table-cell"><span>Client</span></th>
                      <th className="tb-odr-info text-end"><span>Amount</span></th>
                      <th className="tb-odr-info text-center d-none d-sm-table-cell"><span>Date</span></th>
                      <th className="tb-odr-info text-center pe-3"><span>Status</span></th>
                    </tr>
                  </thead>
                  <tbody className="tb-odr-body">
                    {deals.map((deal) => {
                      const dealId = deal._id || deal.id;
                      return (
                        <tr
                          className="tb-odr-item deal-row-hover" // Added a class for hover if preferred over JS
                          key={dealId}
                          style={clickableRowStyle}
                          onClick={() => handleRowClick(dealId)}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = hoverRowStyleJS.backgroundColor}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = ''}
                          title="Click to view deal details"
                        >
                          <td className="tb-odr-info bg-white text-start ps-3">
                            {
                              [newRoles.Admin, newRoles.MasterAdmin].includes(authUser.role) && deniedPermissions?.dealDetails ? (
                                <span>{deal?.reference_number}</span>
                              ) : [newRoles.Admin, newRoles.MasterAdmin].includes(authUser.role) ? (
                                <Link to={`/deal-details/${deal.id ?? deal._id}`}>
                                  {deal?.reference_number}
                                </Link>
                              ) : authUser.role === newRoles.TeamLeader ?
                                (authUser?.id === deal?.assignedAgent?.userId) ? (
                                  <Link to={`/deal-details/${deal.id ?? deal._id}`}>
                                    {deal?.reference_number}
                                  </Link>
                                ) : checkIfLeadsBelongsToAuthUser(deal) ? <Link to={`/deal-details/${deal.id ?? deal._id}`}>
                                  {deal?.reference_number}
                                </Link> : (<span >{deal?.reference_number}</span>) : <Link to={`/deal-details/${deal.id ?? deal._id}`}>
                                  {deal?.reference_number}
                                </Link>
                            }
                          </td>
                          <td className="tb-odr-info bg-white text-start d-none d-md-table-cell">
                            <span className="text-wrap">
                              {deal.property?.title ||
                                deal.propertyTitle ||
                                deal.propertyList?.propertyTitle ||
                                (deal.propertyList && Array.isArray(deal.propertyList) && deal.propertyList[0]?.propertyTitle) ||
                                (deal.unitDetails?.propertyType ? `${deal.unitDetails.propertyType} Unit` : 'N/A')}
                              {deal.propertyList?.propertyType?.propertyType ||
                                (deal.propertyList && Array.isArray(deal.propertyList) && deal.propertyList[0]?.propertyType?.propertyType) ?
                                ` (${deal.propertyList?.propertyType?.propertyType ||
                                (deal.propertyList && Array.isArray(deal.propertyList) && deal.propertyList[0]?.propertyType?.propertyType)})` : ''}
                            </span>
                          </td>
                          <td className="tb-odr-info bg-white text-start d-none d-lg-table-cell">
                            {deal.client?.name ||
                              deal.clientName ||
                              deal.user?.name ||
                              (deal.user_id && typeof deal.user_id === 'object' ? deal.user_id.name : '') ||
                              (deal.buyerInternalUser ?
                                (deal.sellerInternalUser ?
                                  `${deal.buyerInternalUser.first_name || ''} ${deal.buyerInternalUser.last_name || ''} / ${deal.sellerInternalUser.first_name || ''} ${deal.sellerInternalUser.last_name || ''}`.trim() :
                                  `${deal.buyerInternalUser.first_name || ''} ${deal.buyerInternalUser.last_name || ''}`.trim()
                                ) :
                                (deal.sellerInternalUser ?
                                  `${deal.sellerInternalUser.first_name || ''} ${deal.sellerInternalUser.last_name || ''}`.trim() :
                                  'N/A'
                                )
                              )}
                          </td>
                          <td className="tb-odr-info bg-white text-end">
                            <span className="fw-bold">
                              {formatCurrency(
                                deal.amount ||
                                deal.dealAmount ||
                                deal.agreed_salePrice ||
                                deal.estimated_price ||
                                0
                              )}
                            </span>
                            {deal.purpose && <span className="small text-muted d-block">{`(${deal.purpose})`}</span>}
                          </td>
                          <td className="tb-odr-info bg-white text-center d-none d-sm-table-cell">
                            {formatDate(deal.date || deal.createdAt || deal.creation_date)}
                          </td>
                          <td className="tb-odr-info bg-white text-center pe-3">
                            <RenderRecentDealStatusBadge status={deal.status} />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </Block>
      </Block>
    </div>
  );
};

export default RecentDeals;
